import React, {useEffect, useRef, useState} from 'react';
import Translate from "../../../../../../../components/Translate";
import ColorIndicator from "../../../../../../../components-new/ColorIndicator";
import {
    calculateRank,
    filterInputStyle,
    formatSecondsToHHMMSS,
    globalExportToExcel
} from "../../../../../../../utils/General";
import Input from "../../../../../../../components-new/Input";
import ExcelButton from "../../../../../../../components-new/buttons/ExcelButton";
import RatingStars from "../../../../../../../components/rating-stars/RatingStars";
import Tooltip from "../../../../../../../components-new/Tooltip";
import ResetButton from "../../../../../../../components-new/buttons/ResetButton";
import TranslateProperty from "../../../../../../../components/TranslateProperty";
import {getOperatorsTableData} from "../../../../../../../services/statistics/api";
import LoadingComponent from "../../../../../../../assets/LoadingComponent";
import TableHeaderSort from "../../../../../../../components-new/Table/TableHeaderSort";

const tabOptions = {
    general: 'main',
    requests: 'requests',
    time: 'time',
};
const tableHeaders = {
    'main': [
        {text: 'operator', sortColumn: 'firstName,lastName'},
        {text: 'currentStatus', sortColumn: 'currentStatus', align: 'center'},
        {text: 'Rating', sortColumn: 'rating', align: 'center'},
        {text: 'currentChatLimit', sortColumn: 'currentChatLimit', align: 'center'},
        {text: 'chatLimit', sortColumn: 'chatLimit', align: 'center'},
    ],
    'requests': [
        {text: 'operator', sortColumn: 'firstName'},
        {text: 'allRequests', sortColumn: 'allChats', align: 'center'},
        {text: 'todaysRequests', sortColumn: 'todaysChats', align: 'center'},
        {text: 'answeredRequestsToOperator', sortColumn: 'answeredChats', align: 'center'},
        {text: 'unansweredRequestsToOperator', sortColumn: 'unansweredChats', align: 'center'},
        {text: 'activeRequests', sortColumn: 'activeChats', align: 'center'},
        {text: 'forwardedRequestsToOperator', sortColumn: 'forwardedChats', align: 'center'},
    ],
    'time': [
        {text: 'operator', sortColumn: 'firstName'},
        {text: 'avrgAnswerSpeed', sortColumn: 'avrgAnswerSpeed', align: 'center'},
        {text: 'avrgFirstAnswerSpeed', sortColumn: 'avrgFirstAnswerSpeed', align: 'center'},
        {text: 'avrgConvTime', sortColumn: 'avrgConvTime', align: 'center'},
    ]
};

const OperatorsTable = ({
                            openTrModal,
                            company
                        }) => {
    const [activeTab, setActiveTab] = useState(tabOptions.general);

    const [filters, setFilters] = useState({
        companyIds: company.subs ? company.subs.map(sub => sub.id) : [],
        start: '',
        end: '',
        operator: ''
    })

    const [pageable, setPageable] = useState({sort: ''});
    const [isFetching, setIsFetching] = useState(false);
    const [operators, setOperators] = useState([]);
    const [operatorSearchValue, setOperatorSearchValue] = useState('')

    let updateFilterOnSearchChange = useRef(false);

    useEffect(() => {
        fetchData();
    }, [pageable.sort, filters]);

    useEffect(() => {

        //without this logic on reset filters makes 2 api calls
        if (!updateFilterOnSearchChange.current) {
            updateFilterOnSearchChange.current = true;
            return
        }

        const timeout = setTimeout(() => {
            updateFilter('operator', operatorSearchValue);
        }, 300)

        return () => {
            clearTimeout(timeout);
        }
    }, [operatorSearchValue])

    const handleSort = (column) => {
        if (!column) return

        const sort = pageable.sort;
        let newSort
        let currentColumn, currentOrder

        if (sort) {
            currentOrder = sort.split(',').pop();
            currentColumn = sort.split(',').slice(0, -1).join(',');
        }

        if (currentOrder && currentColumn === column) {
            newSort = column + ',' + (currentOrder === 'asc' ? 'desc' : 'asc');
        } else {
            newSort = column + ',' + 'asc'
        }

        setPageable({
            ...pageable,
            sort: newSort,
        });
    };

    function updateFilter(key, val) {
        setFilters({
            ...filters,
            [key]: val
        })
    }

    function fetchData() {
        setIsFetching(true);

        getOperatorsTableData(company.id, filters, pageable).then((res) => {
            if (res) {
                setOperators(res);
            } else {
                setOperators([])
            }
        }).catch(e => {
            setOperators([])
        }).finally(() => {
            setIsFetching(false);
        })
    }

    function resetFilters() {
        updateFilterOnSearchChange.current = false
        setOperatorSearchValue('')

        setFilters({
            ...filters,
            start: '',
            end: '',
            operator: ''
        })
    }

    const exportToExcel = () => {
        let columns = [];

        if (activeTab === tabOptions.general) {
            columns = [
                {label: 'Operator', value: row => `${row.firstName} ${row.lastName}`},
                {label: 'Status', value: 'currentStatus'},
                {label: 'Status Time', value: 'currentStatusTime'},
                {
                    label: 'Rating / Total',
                    value: row => {
                        const total = row.goodAnswer + row.badAnswer;
                        const rating = total ? parseInt((row.goodAnswer * 100) / total) : 0;
                        return `${rating}/${total}`;
                    },
                },
                {
                    label: 'Current Chat Limit',
                    value: row => row.currentChatLimit >= 9999 ? 'Unlimited' : row.currentChatLimit
                },
                {
                    label: 'Chat Limit',
                    value: row => row.chatLimit >= 9999 ? 'Unlimited' : row.chatLimit
                },
            ];
        } else if (activeTab === tabOptions.requests) {
            columns = [
                {label: 'Operator', value: row => `${row.firstName} ${row.lastName}`},
                {label: 'All Chats', value: 'allChats'},
                {label: 'Answered', value: 'answeredChats'},
                {label: 'Unanswered', value: 'unansweredChats'},
                {label: 'Active', value: 'activeChats'},
                {label: 'Chats Today', value: 'todaysChats'},
                {label: 'Forwarded', value: 'forwardedChats'},
            ];
        } else if (activeTab === tabOptions.time) {
            columns = [
                {label: 'Operator', value: row => `${row.firstName} ${row.lastName}`},
                {
                    label: 'Avg Answer Speed',
                    value: row => formatSecondsToHHMMSS(row.avrgAnswerSpeed),
                },
                {
                    label: 'Avg First Answer Speed',
                    value: row => formatSecondsToHHMMSS(row.avrgFirstAnswerSpeed),
                },
                {
                    label: 'Avg Conversation Time',
                    value: row => formatSecondsToHHMMSS(row.avrgConvTime),
                },
            ];
        }

        globalExportToExcel({
            data: operators,
            columns,
            fileName: `Operators-${activeTab}.xlsx`,
        });
    };

    const renderTabButtons = () => (
        <div className="tab-buttons" style={{justifyContent: 'start'}}>
            {Object.entries(tabOptions).map(([key, value]) => (
                <button
                    key={key}
                    className={`tab-button ${activeTab === value ? 'active' : ''}`}
                    onClick={() => setActiveTab(value)}
                >
                    <Translate text={value}/>
                </button>
            ))}
        </div>
    );

    const renderHeader = () => {
        const headers = tableHeaders[activeTab];

        if (!headers) return null;

        return (
            <tr>
                {headers.map((header, index) => (
                    <th
                        key={header.text}
                        style={{textAlign: header.align || 'left', cursor: 'pointer'}}
                        onClick={() => handleSort(header.sortColumn)}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: index === 0 ? 'flex-start' : 'center',
                            gap: '10px'
                        }}>
                            <Translate text={header.text}/>
                            {
                                header.sortColumn &&

                                <TableHeaderSort
                                    isActive={pageable.sort.split(',').slice(0, -1).join(',') === header.sortColumn}
                                    direction={pageable.sort.split(',').pop()}
                                />
                            }
                        </div>

                    </th>
                ))}
            </tr>
        );
    };

    const renderRows = () => {
        return operators.map((operator, index) => {

            const fullName = `${operator.firstName} ${operator.lastName}`;

            const ratingInStars = (operator.rating * 5) / 100;


            const rank = calculateRank({
                ratingPercent: operator.rating,
                avrgAnswerSpeed: operator.avrgAnswerSpeed,
                answeredChatsCount: operator.answeredChats,
            })

            if (activeTab === tabOptions.general) {
                return (
                    <tr key={index}>
                        <td>
                            {fullName}
                        </td>
                        <td style={{textAlign: 'center'}}>
                              <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: '#f9f9f9',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  color: '#333'
                              }}>

                                  <ColorIndicator currentStatus={operator.currentStatus}/>
                                  <Translate text={operator.currentStatus}/> ({operator.currentStatusTime})
                              </span>
                        </td>
                        <td style={{textAlign: 'center', cursor: 'pointer'}}>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#f9f9f9',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    color: '#333'
                                }}
                            >
                                <RatingStars value={ratingInStars}/>
                                <div style={{display: 'flex', gap: '5px'}}>
                                    <Tooltip text={'Rating'}>{operator.rating}</Tooltip>
                                    /
                                    <Tooltip text={'ratingCount'}>{operator.goodAnswer + operator.badAnswer}</Tooltip>
                                    /
                                    <Tooltip text={'rank'}>{rank}</Tooltip>
                                </div>
                            </div>
                        </td>
                        <td style={{textAlign: 'center'}}>{operator.currentChatLimit >= 9999 ?
                            <Translate text={'unlimited'}/> : operator.currentChatLimit}</td>
                        <td style={{textAlign: 'center'}}>{operator.chatLimit >= 9999 ?
                            <Translate text={'unlimited'}/> : operator.chatLimit}</td>
                    </tr>
                );
            }

            if (activeTab === tabOptions.requests) {
                return (
                    <tr key={index}>
                        <td>{fullName}</td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center'}}
                            onClick={() => openTrModal('allRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.allChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center'}}
                            onClick={() => openTrModal('todaysRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.todaysChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center', color: '#22c55e'}}
                            onClick={() => openTrModal('answeredRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                unanswered: false,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.answeredChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center', color: '#E14E52'}}
                            onClick={() => openTrModal('unansweredRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                unanswered: true,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.unansweredChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center'}}
                            onClick={() => openTrModal('activeRequests', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                statuses: 'OPERATOR_PROCESSING',
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.activeChats}
                        </td>

                        <td
                            className="clickable-td"
                            style={{textAlign: 'center'}}
                            onClick={() => openTrModal('forwardedRequestsToOperator', {
                                operatorId: operator.id,
                                operator: operator.firstName + ' ' + operator.lastName,
                                forwarded: true,
                                end: filters.end,
                                start: filters.start
                            })}
                        >
                            {operator.forwardedChats}
                        </td>

                    </tr>
                );
            }

            if (activeTab === tabOptions.time) {
                return (
                    <tr key={index}>
                        <td>{fullName}</td>
                        <td style={{textAlign: 'center'}}>{formatSecondsToHHMMSS(operator.avrgAnswerSpeed)}</td>
                        <td style={{textAlign: 'center'}}>{formatSecondsToHHMMSS(operator.avrgFirstAnswerSpeed)}</td>
                        <td style={{textAlign: 'center'}}>{formatSecondsToHHMMSS(operator.avrgConvTime)}</td>
                    </tr>
                );
            }

            return null;
        });
    };

    return (
        <div style={{width: '100%'}}>
            <div>
                {renderTabButtons()}
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                marginBottom: '16px',
                flexWrap: 'wrap',
            }}>
                <div style={{display: 'flex', gap: '10px', flexWrap:'wrap'}}>
                    <TranslateProperty properties={[{
                        key: 'placeholder',
                        value: 'searchByOperator'
                    }]}>
                        <input
                            type="text"
                            value={operatorSearchValue}
                            onChange={(e) => setOperatorSearchValue(e.target.value)}
                            style={{...filterInputStyle, width: '300px'}}
                        />
                    </TranslateProperty>
                    {(activeTab === tabOptions.requests || activeTab === tabOptions.time) &&
                        <React.Fragment>
                            <Input
                                className={'calendar-select'}
                                style={{width: '240px', height: '40px'}}
                                type="date"
                                value={filters.start}
                                onChange={e => updateFilter('start', e.target.value)}
                            />
                            <Input
                                className={'calendar-select'}
                                style={{width: '240px', height: '40px'}}
                                type="date"
                                value={filters.end}
                                onChange={e => updateFilter('end', e.target.value)}
                            />
                        </React.Fragment>
                    }
                    {
                        (filters.start !== '' || filters.end !== '' || filters.operator !== '')
                        &&
                        <ResetButton
                            onClick={resetFilters}
                            width={150}
                            height={40}
                            variant="outline"
                        />
                    }

                </div>

                <div>
                    <ExcelButton
                        onClick={exportToExcel}
                        width={190}
                        height={40}
                        variant="outline"
                        children={
                            <span style={{fontSize: '14px', backgroundColor: 'transparent'}}>
                                        <Translate text={'downloadXls'}/>
                                    </span>
                        }
                    />
                </div>
            </div>

            <div style={{overflowX: 'auto', maxHeight: '400px'}}>
                <table className={"custom-table"}>
                    <thead>{renderHeader()}</thead>
                    <tbody>
                    {(isFetching) ? (
                        <tr>
                            <td colSpan="100%" style={{padding: '50px 0'}}><LoadingComponent/></td>
                        </tr>
                    ) : (
                        renderRows()
                    )}

                    {
                        operators.length === 0 && !isFetching &&
                        <tr>
                            <td colSpan="100%" style={{textAlign: 'center', padding: '40px', color: '#6c757d'}}>
                                <Translate text={'noResultsFound'}/>
                            </td>
                        </tr>
                    }
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default OperatorsTable;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/statistics/components/operators/OperatorsTable.jsx